import { useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Stack,
  SxProps,
  Typography
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Error, WarningAmber } from '@mui/icons-material';
import { Button } from 'system/atoms';
import { DropdownList } from 'system';
import { CustomFieldSchema, FieldProps, FieldWrapper } from 'common/form';
import { UseUserCalendarReturnData } from 'modules/calendar/hooks/useUserCalendar';
import {
  UserCalendarProvider,
  UserCalendarStatus
} from 'generated-graphql/types';
import { TrashIcon } from 'components/Icons/TrashIcon';
import { getInitials } from 'modules/calendar/utils/getInitials';

type Props = FieldProps<CustomFieldSchema> & {
  userCalendars: UseUserCalendarReturnData;
  ownerId: string | null;
  isRoundRobin: boolean;
  onOrganizerChange: (id: string) => void;
  // The backend rejects an organizer change once published.
  lockOrganizer: boolean;
  // Stored members matching no reachable calendar: no id to re-submit, so
  // naming them is the whole remedy.
  unresolvedMembers?: { email: string; name: string }[];
  showPanelTypeHint?: boolean;
};

type AddOption = { label: string; value: string };

const triggerSx: SxProps = {
  backgroundColor: 'common.white',
  justifyContent: 'space-between',
  fontWeight: 'normal',
  borderRadius: '8px'
};

const rowSx: SxProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  py: 1,
  borderBottom: '1px solid',
  borderColor: 'customGrey.light'
};

const iconSx: SxProps = { fontSize: '16px' };

const avatarSx: SxProps = {
  width: 30,
  height: 30,
  fontSize: '12px',
  fontWeight: 600,
  bgcolor: 'primary.main'
};

const personSx: SxProps = { flex: 1 };

const actionsSx: SxProps = { flexShrink: 0 };

const emailSx: SxProps = { color: 'text.secondary' };

const calendarLabel = (calendar: UseUserCalendarReturnData[number]) =>
  calendar.provider === UserCalendarProvider.Virtual
    ? `${calendar.name} (Virtual)`
    : calendar.name;

// One list for owner + panel, so it writes two form values: `owner` through
// onOrganizerChange and `participants` through the field's own onChange.
export const MeetingPanel = ({
  value,
  onChange,
  field,
  errorMessage,
  userCalendars,
  ownerId,
  isRoundRobin,
  onOrganizerChange,
  lockOrganizer,
  unresolvedMembers = [],
  showPanelTypeHint = false
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const anchorRef = useRef<HTMLButtonElement>(null);
  const orderRef = useRef<string[]>([]);

  const participants: string[] = value ?? [];
  const organizerId = !isRoundRobin && ownerId ? ownerId : null;

  const memberIds = [
    ...(organizerId ? [organizerId] : []),
    ...participants.filter((id) => id !== organizerId)
  ];
  // Rows keep insertion order, so promoting someone moves the marker not the row.
  const orderedIds = [
    ...orderRef.current.filter((id) => memberIds.includes(id)),
    ...memberIds.filter((id) => !orderRef.current.includes(id))
  ];
  orderRef.current = orderedIds;

  const rows = orderedIds.map((id) => ({
    id,
    isOrganizer: id === organizerId
  }));
  const selectedIds = new Set(memberIds);

  // Rows render every status so a disconnected member still shows.
  const addOptions: AddOption[] = userCalendars
    .filter((calendar) => calendar.status === UserCalendarStatus.Connected)
    .map((calendar) => ({
      label: calendarLabel(calendar),
      value: calendar.id
    }));

  const filteredOptions = addOptions.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const findCalendar = (id: string) =>
    userCalendars.find((calendar) => calendar.id === id);

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  const toggleInterviewer = (id: string) => {
    if (!id) {
      return;
    }

    // Same guard the row's trash icon uses: a locked organizer can't be dropped.
    if (lockOrganizer && id === organizerId) {
      return;
    }

    if (selectedIds.has(id)) {
      remove(id);
      return;
    }

    // Not on edit: the API rejects an organizer change, so this could never save.
    if (!isRoundRobin && !organizerId && !lockOrganizer) {
      onOrganizerChange(id);
    } else {
      onChange([...participants, id]);
    }
  };

  const makeOrganizer = (id: string) => {
    const remaining = participants.filter((participant) => participant !== id);
    onChange(organizerId ? [organizerId, ...remaining] : remaining);
    onOrganizerChange(id);
  };

  const remove = (id: string) => {
    if (id === organizerId) {
      // The organizer can't be left empty while a panel exists.
      const [next, ...rest] = participants;
      onChange(next ? rest : participants);
      onOrganizerChange(next ?? '');
    } else {
      onChange(participants.filter((participant) => participant !== id));
    }
  };

  return (
    <FieldWrapper
      dataTestId="meeting-panel"
      label={field.label}
      subLabel={field.subLabel}
    >
      <Button
        ref={anchorRef}
        data-testid="add-interviewer"
        fullWidth
        variant="secondary"
        type="button"
        state={errorMessage ? 'destructive' : 'hover'}
        endIcon={<KeyboardArrowDownIcon />}
        sx={triggerSx}
        onClick={() => (isOpen ? closeDropdown() : setIsOpen(true))}
      >
        {memberIds.length > 0
          ? `${memberIds.length} selected`
          : 'Add an interviewer'}
      </Button>

      <DropdownList
        popperProps={{
          open: isOpen,
          anchorEl: anchorRef.current,
          placement: 'bottom-start',
          style: { width: anchorRef.current?.offsetWidth, zIndex: 1300 }
        }}
        options={filteredOptions}
        selectedValues={memberIds}
        emptyMessage="No connected calendars in your account"
        searchOptions={{
          placeholder: 'Search by contact name',
          searchQuery,
          onChange: setSearchQuery
        }}
        onSelect={toggleInterviewer}
        onClickAway={closeDropdown}
      />

      {rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'customGrey.dark', mt: 1.5 }}>
          No interviewers yet. Choose one from the list above.
        </Typography>
      ) : (
        <Stack sx={{ mt: 1.5 }}>
          {rows.map((row) => {
            const calendar = findCalendar(row.id);
            return (
              <Box key={row.id} sx={rowSx}>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  sx={personSx}
                >
                  <Avatar sx={avatarSx}>
                    {getInitials(calendar?.name ?? '')}
                  </Avatar>
                  <Typography variant="body2" sx={{ flexShrink: 0 }}>
                    {calendar ? calendarLabel(calendar) : row.id}
                  </Typography>
                  {calendar?.email && (
                    <Typography variant="body2" sx={emailSx}>
                      {calendar.email}
                    </Typography>
                  )}
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  sx={actionsSx}
                >
                  {calendar &&
                    calendar.status !== UserCalendarStatus.Connected && (
                      <Chip
                        icon={<Error sx={iconSx} />}
                        label="Calendar disconnected"
                        size="small"
                        color="error"
                      />
                    )}
                  {!isRoundRobin &&
                    (row.isOrganizer ? (
                      <Chip label="Organiser" size="small" color="success" />
                    ) : (
                      !lockOrganizer && (
                        <Button
                          variant="link"
                          aria-label={`Make ${
                            calendar ? calendar.name : row.id
                          } the organiser`}
                          onClick={() => makeOrganizer(row.id)}
                        >
                          Make organiser
                        </Button>
                      )
                    ))}
                  {!(lockOrganizer && row.isOrganizer) && (
                    <IconButton
                      aria-label={`Remove ${
                        calendar ? calendarLabel(calendar) : row.id
                      } from the panel`}
                      size="small"
                      onClick={() => remove(row.id)}
                    >
                      <TrashIcon type="error" />
                    </IconButton>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}

      {showPanelTypeHint && rows.length > 0 && (
        <Typography variant="body2" sx={{ color: 'customGrey.dark', mt: 1.5 }}>
          Add a second interviewer to choose how the panel is scheduled.
        </Typography>
      )}

      {unresolvedMembers.length > 0 && (
        <Stack data-testid="unresolved-panel-members" sx={{ mt: 2 }}>
          {unresolvedMembers.map((member) => (
            <Box key={member.email} sx={rowSx}>
              <Box sx={personSx}>
                <Typography variant="body2">
                  {member.name || member.email}
                </Typography>
                {member.name && (
                  <Typography variant="body2" sx={emailSx}>
                    {member.email}
                  </Typography>
                )}
              </Box>
              <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                sx={actionsSx}
              >
                <WarningAmber sx={{ ...iconSx, color: 'warning.dark' }} />
                <Typography variant="body2" sx={{ color: 'warning.dark' }}>
                  Popp can&apos;t reach this person. They&apos;ll be removed
                  when you save.
                </Typography>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      {errorMessage && (
        <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 1 }}>
          <Error color="error" sx={iconSx} />
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            {errorMessage}
          </Typography>
        </Stack>
      )}
    </FieldWrapper>
  );
};
